import { useOptimisticAssetDetails } from '@/app/assets/details/hooks/useOptimisticAssetDetails';
import { deleteRawModelTag, updateRawModel } from '@/app/assets/actions/rawModel';
import { AssetTag } from '@/types/rawModel';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MAIN_ASSET_QUERY_KEY, useSelectedAsset } from '../../hooks/useAssetsQuery';
import { TAGS_QUERY_KEY } from '../hooks/useTagsQuery';
import { addTagsToRapidModel, deleteRapidModelTag } from '../../actions/rapidModel';
import { getPublishedPage } from '@/app/published/actions/getPublishedModels';
import { PublishedModel } from '@/app/published/interfaces/api';

export const useTagMutations = () => {
  const clientQuery = useQueryClient();
  const selectedAsset = useSelectedAsset();
  const { onAddTag, onRemoveTag } = useOptimisticAssetDetails(selectedAsset);

  const handleTagAdd = async (tag: string) => {
    if (!selectedAsset?.id) {
      return;
    }
    onAddTag(tag);
    const tags = [...(selectedAsset?.tags ?? []).map((t) => t.name), tag];

    if (selectedAsset.assetType === 'raw') {
      await updateRawModel(selectedAsset.id, { tags });
    } else {
      await addTagsToRapidModel(selectedAsset.id, tags);
    }

    clientQuery.invalidateQueries({
      predicate: (query) => query.queryKey.includes(MAIN_ASSET_QUERY_KEY) || query.queryKey.includes(TAGS_QUERY_KEY),
    });
  };

  const handleTagDeletion = async (tag: AssetTag) => {
    if (!selectedAsset?.id) {
      return;
    }
    await clientQuery.cancelQueries({
      predicate: (query) => query.queryKey.includes(MAIN_ASSET_QUERY_KEY),
    });

    onRemoveTag(tag.id);
    if (selectedAsset.assetType === 'output') {
      await deleteRapidModelTag(selectedAsset.id, tag.id);
    } else {
      await deleteRawModelTag(selectedAsset.id, tag.id);
    }

    clientQuery.invalidateQueries({
      predicate: (query) => query.queryKey.includes(MAIN_ASSET_QUERY_KEY) || query.queryKey.includes(TAGS_QUERY_KEY),
    });
  };

  return {
    handleTagAdd,
    handleTagOnDelete: handleTagDeletion,
  };
};
export const ASSET_EMBEDS_KEY = 'AssetEmbeds';
export const useAssetEmbeds = (assetId: number, showOneClick?: boolean) =>
  useQuery({
    queryKey: [ASSET_EMBEDS_KEY, assetId, showOneClick || false],
    queryFn: async () => {
      let allData: PublishedModel[] = [];
      let page = 1;
      let lastPage = 1;

      do {
        const response = await getPublishedPage({ page, rapidId: assetId, showOneClick });
        allData = allData.concat(response.data);
        lastPage = response.meta.last_page;
        page++;
      } while (page <= lastPage);

      return allData;
    },
    throwOnError: false,
  });
